import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

const STALE_THRESHOLD_DAYS = 7;

function isStale(timestamp) {
  if (!timestamp) return true;
  const ts = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const diffDays = (Date.now() - ts.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays > STALE_THRESHOLD_DAYS;
}

export default function AISummary({ companyId, companyName, role }) {
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    async function loadSummary() {
      try {
        const summaryDoc = await getDoc(doc(db, 'aiSummaries', companyId));
        if (summaryDoc.exists()) {
          const data = summaryDoc.data();
          setSummary(data.companySummary);
          setLastUpdated(data.lastGeneratedAt);
          // Auto-refresh if stale
          if (isStale(data.lastGeneratedAt)) {
            generateSummary(companyId);
          }
        } else {
          // No summary yet — generate one
          generateSummary(companyId);
        }
      } catch (err) {
        console.error(err);
        setError('Unable to load summary.');
      } finally {
        setLoading(false);
      }
    }
    loadSummary();
  }, [companyId]);

  const generateSummary = async (cId) => {
    setGenerating(true);
    setError(null);
    try {
      // Gather all company data
      const [compDoc, assessSnap, sessionSnap, workPlanSnap, canvasSnap, journeySnap] = await Promise.all([
        getDoc(doc(db, 'companies', cId)),
        getDocs(collection(db, 'companies', cId, 'assessments')),
        getDocs(collection(db, 'companies', cId, 'coachingSessions')),
        getDocs(collection(db, 'companies', cId, 'workPlans')),
        getDocs(collection(db, 'companies', cId, 'canvasVersions')),
        getDocs(query(collection(db, 'companies', cId, 'canvasVersions'), where('type', '==', 'cjm'))),
      ]);

      const company   = compDoc.data();
      const assessments = assessSnap.docs.map(d => d.data());
      const sessions    = sessionSnap.docs.map(d => d.data())
        .sort((a,b) => (b.sessionDate?.seconds||0) - (a.sessionDate?.seconds||0))
        .slice(0, 5); // last 5 sessions
      const workPlans   = workPlanSnap.docs.map(d => d.data());
      const canvasVersions = canvasSnap.docs
        .map(d => d.data())
        .filter(v => v.type === 'bmc')
        .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
      const latestCanvas = canvasVersions[0] || null;
      const journeyVersions = journeySnap.docs
        .map(d => d.data())
        .filter(v => v.type === 'cjm')
        .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
      const latestJourney = journeyVersions[0] || null;

      const baseline  = assessments.find(a => a.type === 'baseline');
      const target    = assessments.find(a => a.type === 'target');
      const periodics = assessments.filter(a => a.type === 'periodic')
        .sort((a,b) => (b.assessmentDate?.seconds||0) - (a.assessmentDate?.seconds||0));
      const latest    = periodics[0];

      // Build context for Claude
      const context = `
Company: ${company.name}
Description: ${company.description || 'N/A'}
Country: ${company.country}, Sector: ${company.sector}

SCALING STAGE:
- Baseline (Immersion Week): ${baseline?.stage || company.currentStage} — ${baseline?.stageUpgrade || company.currentStageUpgrade}, ${baseline?.stageProgress || company.currentStageProgress}% through stage
- Latest assessment: ${latest ? `${latest.stage} — ${latest.stageUpgrade}, ${latest.stageProgress}% through stage` : 'No periodic assessment yet'}
- Target (Dec 2026): ${target?.stage || company.targetStage} — ${target?.stageUpgrade || company.targetStageUpgrade}

RECENT COACHING SESSIONS (last ${sessions.length}):
${sessions.map((s, i) => {
  const date = s.sessionDate?.toDate ? s.sessionDate.toDate().toLocaleDateString('en-GB') : 'Unknown date';
  const openActions = (s.actionItems||[]).filter(a => a.status === 'open').length;
  return `Session ${i+1} (${date}): ${s.keyPoints?.slice(0,200) || 'No notes'}${s.decisions ? ` | Decisions: ${s.decisions.slice(0,100)}` : ''}${openActions > 0 ? ` | ${openActions} open actions` : ''}`;
}).join('\n') || 'No sessions logged yet'}

WORK PLANS:
${workPlans.length > 0 ? workPlans.map(w =>
  `${w.bucketLabel}: Plan — ${w.plan?.slice(0,150)||'N/A'}. Outcome — ${w.outcome?.slice(0,150)||'Not yet reported'}. Scaling link — ${w.scalingLink?.slice(0,100)||'N/A'}`
).join('\n') : 'No work plans recorded yet'}

BUSINESS MODEL CANVAS (latest version):
${(() => { if (!latestCanvas) return 'No canvas submitted yet'; const blocks = latestCanvas.blocks || {}; return Object.entries(blocks).filter(([k,v]) => v).map(([k,v]) => k + ': ' + v.slice(0,150)).join(' | ') || 'Canvas exists but no blocks filled'; })()}

CUSTOMER & USER JOURNEY MAP (latest version):
${(() => {
  if (!latestJourney) return 'No journey map submitted yet';
  const tracks = [];
  ['customer','user'].forEach(track => {
    const stages = latestJourney.data?.[track] || {};
    const entries = Object.entries(stages).map(([stageId, fields]) =>
      stageId + ': ' + Object.entries(fields||{}).filter(([,v])=>v).map(([k,v]) => k + '=' + v.slice(0,80)).join('; ')
    ).filter(Boolean);
    if (entries.length) tracks.push(track.toUpperCase() + ' - ' + entries.join(' | '));
  });
  return tracks.join('\n') || 'Journey map exists but no content yet';
})()}

COACH NOTES ON ASSESSMENTS:
${assessments.filter(a => a.coachNotes).map(a => `${a.type}: ${a.coachNotes?.slice(0,200)}`).join('\n') || 'No coach notes yet'}
`.trim();

      const prompt = `You are a programme analyst at an enterprise support organisation. Based on the data below, write a concise 4-6 paragraph narrative summary about this company's progress in the Scaling Readiness Programme.

Your summary should:
1. Open with where the company currently stands in their scaling journey and what has changed since the immersion week baseline
2. Highlight the most significant developments from recent coaching sessions and work plan delivery
3. Call out the key strengths and the main areas that need attention or acceleration
4. Comment on how well they are tracking toward their December 2026 target
5. Close with a forward-looking sentence on what the next 4-8 weeks should focus on

Write in clear, professional language suitable for both the company team and programme funders. Do not use bullet points — write in flowing paragraphs. Do not fabricate specific numbers or dates that aren't in the data.

${context}`;

      // Call Claude API
      // Call via Firebase Cloud Function
      const generateFn = httpsCallable(functions, 'generateAISummary', { timeout: 120000 });
      const result = await generateFn({ prompt, companyId: cId });
      const generatedSummary = result.data?.summary || 'Summary generation failed.';

      // Store in Firestore
      await setDoc(doc(db, 'aiSummaries', cId), {
        companyId:        cId,
        companyName:      company.name,
        companySummary:   generatedSummary,
        lastGeneratedAt:  new Date(),
        generatedFromData: {
          assessmentCount:   assessments.length,
          sessionCount:      sessions.length,
          workPlanCount:     workPlans.length,
          latestAssessment:  latest?.stage || null,
          latestProgress:    latest?.stageProgress || null,
        },
      }, { merge: true });

      setSummary(generatedSummary);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('AI summary error:', err);
      setError('Unable to generate summary. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return null;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="gm-ai-summary">
      <div className="gm-ai-summary-header">
        <div className="gm-ai-summary-title">
          <span className="gm-ai-badge">AI</span>
          Progress summary
        </div>
        <div className="gm-ai-summary-meta">
          {lastUpdated && <span>Updated {formatDate(lastUpdated)}</span>}
          <button
            className="gm-btn gm-btn-sm gm-btn-outline"
            onClick={() => generateSummary(companyId)}
            disabled={generating}
          >
            {generating ? 'Generating…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {(loading || generating) && (
        <div className="gm-ai-summary-loading">
          <div className="gm-spinner" />
          <span>{generating ? 'Generating summary…' : 'Loading…'}</span>
        </div>
      )}

      {error && !generating && (
        <div className="gm-alert gm-alert-error">{error}</div>
      )}

      {summary && !loading && !generating && (
        <div className="gm-ai-summary-body">
          {summary.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}
    </div>
  );
}
